import type { Track } from "../../shared/domain.ts";

/**
 * Searching the Catalog for the Dropdown.
 *
 * The Dropdown is the reason Verdicts are exact: a Guess is always a real
 * Catalog Track, so there is no fuzzy title matching anywhere in the game and
 * "Sweet Child o' Mine" versus "Sweet Child of Mine" is a non-problem.
 */

export function normalize(raw: string): string {
  return raw
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export interface SearchableTrack extends Track {
  /** Precomputed so keystrokes don't re-normalize the whole Catalog. */
  haystack: string;
  normTitle: string;
  normArtist: string;
}

/**
 * The Catalog's one read boundary, so it is also where a Catalog built before a
 * field existed gets defaulted.
 *
 * `hookStart` falls back to 0, which is where every Step started before hook
 * detection. `percentile` falls back to 0.5, which flattens the Daily ramp to a
 * near-uniform draw rather than silently ranking every Track as obscure.
 */
export function indexCatalog(tracks: Track[]): SearchableTrack[] {
  return tracks.map((t) => {
    const normTitle = normalize(t.title);
    const normArtist = normalize(t.artist);
    return {
      ...t,
      percentile: t.percentile ?? 0.5,
      hookStart: t.hookStart ?? 0,
      normTitle,
      normArtist,
      haystack: `${normArtist} ${normTitle}`,
    };
  });
}

function score(track: SearchableTrack, query: string): number {
  // Prefix hits are what the player is usually typing, so they outrank
  // mid-string hits regardless of how well-known the song is.
  if (track.normTitle.startsWith(query)) return 3;
  if (track.normArtist.startsWith(query)) return 2;
  if (track.haystack.includes(query)) return 1;
  return 0;
}

/**
 * How many matches are collected before ranking.
 *
 * A one- or two-character query matches most of the Catalog, so without a cap
 * this allocated and *sorted* an array the size of the Catalog on every
 * keystroke. Fine at 3000 Tracks; at 20,000 it is the first place typing starts
 * to feel sticky. Collecting a bounded pile and ranking that is enough: the cap
 * is far larger than `limit`, so the best 8 are still competing against a wide
 * field rather than the first 8 encountered.
 *
 * The Catalog is stored best-first by recognisability, so an early cut-off keeps
 * the most recognisable candidates rather than an arbitrary slice.
 */
const MAX_CANDIDATES = 400;

export function searchCatalog(
  tracks: SearchableTrack[],
  rawQuery: string,
  limit = 8,
): SearchableTrack[] {
  const query = normalize(rawQuery);
  if (query.length === 0) return [];

  const hits: { track: SearchableTrack; rank: number }[] = [];

  for (const track of tracks) {
    const rank = score(track, query);
    if (rank > 0) {
      hits.push({ track, rank });
      if (hits.length >= MAX_CANDIDATES) break;
    }
  }

  return hits
    .sort((a, b) => {
      if (b.rank !== a.rank) return b.rank - a.rank;
      // Among equally good matches, the more recognisable song is the one the
      // player more likely means.
      return b.track.recognisability - a.track.recognisability;
    })
    .slice(0, limit)
    .map((h) => h.track);
}
